import React, { useCallback } from 'react';
import { Card, Button, Header } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserChip } from 'components/lib/Chips';
import util from 'utils/utils';
import { InviteCardProps } from './types';
import InviteeChip from './InviteeChip';

const IdeaInviteCard = ({ invitation, acceptInvite, rejectInvite, removeInvite }: InviteCardProps) => {
  const { inviterUser, inviteeUser, invitee, challenge, forIdeas = [], createdByCurrentUser } = invitation;

  const accept = useCallback(() => {
    util.confirm('Become idea assessor', `Accept invitation and start assessing ${util.pluralise(forIdeas.length, 'idea', 'ideas')} in "${challenge.name}"?`)
      .then(() => {
        acceptInvite(invitation, () => {
          toast.success('Became idea assessor');
        }, () => toast.error('Error becoming idea assessor'));
      })
      .catch(() => {})
  }, [acceptInvite, invitation, forIdeas, challenge?.name]);

  const reject = useCallback(() => {
    util.confirm('Reject assessment invitation', `Are you sure you want to reject an invitation from ${inviterUser?.profile?.fullName} to assess ${util.pluralise(forIdeas.length, 'idea', 'ideas')} in "${challenge?.name || 'Deleted challenge'}?`)
      .then(() => {
        rejectInvite(invitation, () => {
          toast('Invitation rejected');
        }, () => toast.error('Error rejecting invite'));
      })
      .catch(() => {})
  }, [rejectInvite, invitation, inviterUser?.profile?.fullName, forIdeas, challenge?.name]);

  const remove = useCallback(() => {
    util.confirm('Remove assessment invitation', `Are you sure you want to remove this invitation to ${inviteeUser?.profile?.fullName || invitee} to assess ideas in "${challenge?.name || 'Deleted challenge'}"?`)
      .then(() => {
        removeInvite(invitation, () => {
          toast('Invitation removed');
        }, () => toast.error('Error removing invite'));
      })
      .catch(() => {})
  }, [removeInvite, invitation, challenge?.name, inviteeUser?.profile?.fullName, invitee]);

  if (!challenge) {
    invitation.canAccept = false;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <Header style={{ marginBottom: '0.3em' }} as={Link} disabled={!challenge} to={challenge ? `/challenges/${challenge._id}` : null}>{challenge?.name || "Deleted challenge"}</Header>
        <Card.Description>
          {createdByCurrentUser ? (
            <InviteeChip invitation={invitation} preText='You invited' postText={`to assess ${util.pluralise(forIdeas.length, 'idea', 'ideas')} in this challenge`} />
          ) : (
            <UserChip user={inviterUser} noAvatar postText={`has invited you to assess ${util.pluralise(forIdeas.length, 'idea', 'ideas')} in this challenge`} />
          )}
        </Card.Description>
      </div>
      <Card.Content extra>
        <div className='ui'>
          {invitation.acceptedAt ? (
              <Button basic color='green' as={Link} disabled={!challenge} to={`/challenges/${challenge?._id}/assessments/assigned`}>
                Go to assessments
              </Button>
            ) : (
              <>
                {invitation.canAccept ? (
                  <Button basic color='green' onClick={accept}>
                    Accept
                  </Button>
                ) : null}
                {invitation.canRemove ? (
                  <Button basic color={createdByCurrentUser ? null : 'red'} onClick={createdByCurrentUser ? remove : reject}>
                    {createdByCurrentUser ? 'Remove' : 'Reject'}
                  </Button>
                ) : null}
              </>
            )}
        </div>
      </Card.Content>
    </div>
  );
}

export default IdeaInviteCard;
